'use client';

import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { FlattenedUser } from '../../types/user';
import { useEffect } from 'react';
import { useToast } from '../../hooks/useToast';
import {
	useDocumentSettingsModal_GetDocumentWhereQuery,
	useDocumentSettingsModal_ShareDocumentMutation,
	useDocumentSettingsModal_UpdateDocumentMutation,
} from '#types';
import { gql } from '@apollo/client';
import { DocumentSettingsModalShareList } from './DocumentSettingsModalShareList';
import { MuiModalProps, OnCloseType } from '../../mui/MuiModal/MuiModal';
import { userHasOwnerPermissionsForDoc } from '../../lib/helpers/documents';
import { UseDocumentSettingsModalContactFragment } from '#types';

export const useDocumentSettingsModalContactFragment = gql`
	fragment useDocumentSettingsModalContact on Contact {
		...documentSettingsModalShareListContact
	}
	${ DocumentSettingsModalShareList.fragments
		.documentSettingsModalShareListContactFragment }
`;

export const useDocumentSettingsModalDocumentFragment = gql`
	fragment useDocumentSettingsModalDocument on Document {
		id
		name
		exportable
		...documentSettingsModalShareListCustomDocumentWithDownloadUrl
	}
	${ DocumentSettingsModalShareList.fragments
		.documentSettingsModalShareListCustomDocumentWithDownloadUrlFragment }
`;

export type UseDocumentSettingsModalProps = {
	documentId?: string;
	onDocumentShared?: () => Promise<void>;
	useDocumentSettingsModal_ShareDocumentMutation: typeof useDocumentSettingsModal_ShareDocumentMutation;
	useDocumentSettingsModal_UpdateDocumentMutation: typeof useDocumentSettingsModal_UpdateDocumentMutation;
	useDocumentSettingsModal_GetDocumentWhereQuery: typeof useDocumentSettingsModal_GetDocumentWhereQuery;
} & Pick<MuiModalProps['muiModalProps'], 'onClose'>;

export const useDocumentSettingsModal = ( {
	documentId,
	onDocumentShared,
	useDocumentSettingsModal_ShareDocumentMutation,
	useDocumentSettingsModal_UpdateDocumentMutation,
	useDocumentSettingsModal_GetDocumentWhereQuery,
	onClose,
}: UseDocumentSettingsModalProps ) => {
	const user: FlattenedUser = useSelector( ( state: any ) => state?.user );
	const { showError, showSuccess } = useToast();

	// A dictionary of either userId or organizationId and DocumentOwnerType to denote what kind of ID it is
	const [ sharedWithContacts, setSharedWithContacts ] = useState<
		UseDocumentSettingsModalContactFragment[]
	>( [] );
	const [ sharedWithCustomer, setSharedWithCustomer ] = useState( false );
	const [ exportable, setExportable ] = useState( false );

	const [ shareDocument, { loading: shareDocumentLoading } ] =
		useDocumentSettingsModal_ShareDocumentMutation();
	const [ updateDocument, { loading: updateDocumentLoading } ] =
		useDocumentSettingsModal_UpdateDocumentMutation();

	const {
		data: documentData,
		error: documentError,
		loading: documentLoading,
	} = useDocumentSettingsModal_GetDocumentWhereQuery( {
		variables: { where: { id: documentId || '' } },
		skip: !documentId,
	} );

	useEffect( () => {
		if ( documentError ) {
			showError( documentError );
		}
	}, [ documentError, showError ] );

	const document = documentData?.getDocumentWhere;

	useEffect( () => {
		setExportable( document?.exportable || false );
	}, [ document ] );

	const isOwner = document
		? userHasOwnerPermissionsForDoc( user, document )
		: false;

	const handleClose: OnCloseType = useCallback(
		( e, reason ) => {
			setSharedWithContacts( [] );
			onClose?.( e, reason );
		},
		[ onClose ]
	);

	const handleShareDocument = useCallback(
		async ( documentId: string ) => {
			const response = await shareDocument( {
				variables: {
					where: { id: documentId },
					data: {
						sharedWithContacts: {
							connect: sharedWithContacts.map( ( item ) => ( { id: item.id } ) ),
						},
						sharedWithCustomer,
					},
				},
			} );
			if ( response.errors ) {
				response.errors.forEach( ( error ) => showError( error ) );
				return false;
			} else {
				onDocumentShared?.();
				return true;
			}
		},
		[
			onDocumentShared,
			shareDocument,
			sharedWithCustomer,
			sharedWithContacts,
			showError,
		]
	);

	const handleUpdateDocument = useCallback(
		async ( documentId: string, documentName: string ) => {
			const response = await updateDocument( {
				variables: {
					where: { id: documentId },
					data: { exportable, name: documentName },
				},
			} );
			if ( response.errors ) {
				response.errors.forEach( ( error ) => showError( error ) );
				return false;
			} else {
				return true;
			}
		},
		[
			exportable,
			showError,
			updateDocument
		]
	);

	const handleSave = useCallback(
		async ( e: React.MouseEvent<HTMLButtonElement> ) => {
			if ( document ) {
				const response = await Promise.all( [ handleShareDocument( document.id ), handleUpdateDocument( document.id, document.name || '' ), ] );
				if ( response.every( ( response ) => response ) ) {
					handleClose( e, 'fromCode' );
					showSuccess( 'Document settings have been updated' );
				}
			}
			return false;
		},
		[
			document,
			handleClose,
			handleShareDocument,
			handleUpdateDocument,
			showSuccess,
		]
	);

	const loading =
		documentLoading || shareDocumentLoading || updateDocumentLoading;

	return {
		sharedWithContacts,
		setSharedWithContacts,
		sharedWithCustomer,
		setSharedWithCustomer,
		exportable,
		setExportable,
		loading,
		document,
		isOwner,
		handleClose,
		handleSave,
	};
};
